import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AB_API_PORTFOLIO } from '../constants';
import { ApiClientService } from './api-client.service';

interface PortfolioResponse {
  name: string;
  portfolios: IPortfolio[];
}

interface IPortfolio {
  id: string;
  name: string;
  isSelected: boolean;
  isResident: boolean;
  isDefault: boolean;
  portfolioType: string;
  isMainUser: boolean;
  isValidCif7: boolean;
  defaultAccount?: any;
  portfolioId: any;
}

interface ChangePortfolioRequest {
  portfolioId: string;
  type?: string;
}

interface ChangePortfolioResponse {
  updated: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PortfolioService extends ApiClientService {
  private currentPortfolio = new BehaviorSubject<any>(null);
  private getPortfolio = new ReplaySubject<PortfolioResponse>(null);
  public portfolioChanged = new ReplaySubject<boolean>();

  private selectedPortfolio: IPortfolio;

  private portfolios: PortfolioResponse;

  private isLoading = false;

  constructor(http: HttpClient) {
    super(http, AB_API_PORTFOLIO);
  }

  setCurrentPortfolio(portfolio: IPortfolio): void {
    if (!this.selectedPortfolio || this.selectedPortfolio.id !== portfolio.id) {
      this.currentPortfolio.next(portfolio);
      this.selectedPortfolio = portfolio;
    }
  }

  getCurrentPortfolio(): Observable<any> {
    return this.currentPortfolio.asObservable();
  }

  clearPortfolios(logout: boolean = false) {
    this.getPortfolio.complete();
    this.getPortfolio = new ReplaySubject<PortfolioResponse>(null);
    this.portfolios = undefined;
    if (logout) {
      this.portfolioChanged.next(null);
    }
  }

  getPortfolios(): Observable<PortfolioResponse> {
    if (this.portfolios) {
      return this.getPortfolio.asObservable();
    }

    if (!this.isLoading && !this.portfolios) {
      this.isLoading = true;
      this.get().subscribe(
        res => {
          this.isLoading = false;
          this.portfolios = res;
          this.getPortfolio.next({ ...this.portfolios });
        },
        error => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
    }

    return this.getPortfolio.asObservable();
  }

  getSelectedPortfolio() {
    return this.getPortfolios().pipe(
      map(r => r.portfolios.filter(p => p.isSelected))
    );
  }

  changePortfolio(
    request: ChangePortfolioRequest
  ): Observable<ChangePortfolioResponse> {
    this.portfolioChanged.next(true);
    this.clearPortfolios();
    return this.put(request);
  }
}

export { IPortfolio, PortfolioResponse, ChangePortfolioRequest };
